/**
 * More than one line of music on the same staff.
 *
 * A staff carries several voices whenever two players read off it, or one player plays two
 * things at once: a hymn's soprano and alto, a piano's thumb line, a drum kit's hands and
 * feet. The document has always held them and every layer below this one reads them -- the
 * importer keeps what a file arrives with, the renderer draws a band per voice, the
 * clipboard and note entry write into the one the caret stands in. What was missing was the
 * ability to *start* one, which is what this is.
 *
 * A voice belongs to a bar, not to a staff for the length of the piece. That is how a page
 * of music reads: a divided passage is divided where it is divided, and the bars around it
 * show one line and no spare rests. The cost is that neighbouring bars hold different
 * numbers of voices, so a caret crossing a bar line cannot keep its index -- see
 * {@link sameVoice}, which is what `entry-place` carries it across with.
 *
 * Nothing here is about a keystroke. It takes a score, a caret and a selection, and answers
 * with the command to run and where the caret should then stand, or with a sentence saying
 * why there is neither. The questions both this and `ossia.ts` ask of a bar's list of lines
 * are in `lines.ts`.
 */

import { voice as newVoice } from "../document/create";
import type { EventId } from "../document/ids";
import { capacityOf, measureIndexOf } from "../document/query";
import type { Command } from "../commands/types";
import type { Score } from "../document/types";
import type { Caret } from "./entry-place";
import type { VoiceChange } from "./lines";
import { MAX_VOICES, asOne, barsFor, kept, silentEvents, voicesOn, whereIs } from "./lines";
import type { Selection } from "./selection";

export { MAX_VOICES, ossiasOn, sameVoice, standingIn, voicesOn } from "./lines";
export type { Standing, VoiceChange } from "./lines";

/**
 * Start another voice on the staff the caret stands on.
 *
 * The new voice is silent -- rests filling the bar -- because a voice with nothing in it is
 * not engravable and so cannot be clicked into. The caret comes out standing at the start
 * of it, in the first bar the edit reached, ready to be typed into.
 */
export function addVoice(
  score: Score,
  caret: Caret | null,
  selection: Selection,
): VoiceChange | string {
  if (caret === null) return "click where the voice should go first";
  const at = whereIs(score, caret);
  if (at === null) return "the caret is not standing in a voice";
  if (at.ossia) return "an ossia is one line, not a staff to divide";

  const bars = barsFor(score, caret, selection);
  const commands: Command[] = [];
  let landing: Caret | null = null;

  for (const measureId of bars) {
    const measure = at.part.measures[measureId];
    if (measure === undefined) continue;
    const on = voicesOn(measure, at.staff);
    if (on.length >= MAX_VOICES) {
      return `a staff is not written in more than ${MAX_VOICES} voices`;
    }

    const index = (on[on.length - 1] ?? -1) + 1;
    const capacity = capacityOf(score, measureIndexOf(score, measureId));
    const voices = [...measure.voices];
    voices.splice(index, 0, newVoice(at.staff, silentEvents(capacity)));
    landing ??= { ...caret, measureId, voiceIndex: index, onset: 0 };
    commands.push({
      kind: "setPartMeasure",
      partId: at.part.id,
      measureId,
      measure: { ...measure, voices },
    });
  }

  const command = asOne(commands, "add a voice");
  if (command === null || landing === null) return "there is no bar there to put a voice in";
  return { command, caret: landing };
}

/**
 * Take the caret's voice off its staff, wherever the edit reaches.
 *
 * Everything hung on the notes going with it goes too: a dynamic anchored to one of them
 * would be invisible but still exported, and a slur with one end in the voice has nowhere
 * to be drawn. Chord symbols and figures stand at a tick rather than on a note, so they
 * stay. The last voice on a staff is not removable -- a staff with no voice at all is a bar
 * the engraver cannot set.
 */
export function removeVoice(
  score: Score,
  caret: Caret | null,
  selection: Selection,
): VoiceChange | string {
  if (caret === null) return "click in the voice to be taken out first";
  const at = whereIs(score, caret);
  if (at === null) return "the caret is not standing in a voice";
  if (at.ossia) return "take the ossia off rather than one of its voices";
  if (voicesOn(at.measure, at.staff).length < 2) return "a staff has to keep one voice";

  const gone = new Set<EventId>();
  const commands: Command[] = [];
  let landing: Caret | null = null;

  for (const measureId of barsFor(score, caret, selection)) {
    const measure = at.part.measures[measureId];
    if (measure === undefined) continue;
    const on = voicesOn(measure, at.staff);
    if (on.length < 2) continue;
    const index = on[Math.min(at.ordinal, on.length - 1)];
    const voice = index === undefined ? undefined : measure.voices[index];
    if (index === undefined || voice === undefined) continue;

    for (const event of voice.events) gone.add(event.id);
    const voices = measure.voices.slice();
    voices.splice(index, 1);
    const left = voicesOn({ ...measure, voices }, at.staff);
    landing ??= {
      ...caret,
      measureId,
      voiceIndex: left[Math.min(at.ordinal, left.length - 1)] ?? 0,
      onset: 0,
    };
    commands.push({
      kind: "setPartMeasure",
      partId: at.part.id,
      measureId,
      measure: { ...measure, voices, directions: kept(measure.directions, gone) },
    });
  }

  const spanners = at.part.spanners.filter((span) => !gone.has(span.from) && !gone.has(span.to));
  if (spanners.length !== at.part.spanners.length) {
    commands.push({ kind: "setSpanners", partId: at.part.id, spanners });
  }

  const command = asOne(commands, "take out a voice");
  if (command === null || landing === null) return "there is no voice there to take out";
  return { command, caret: landing };
}
